import { DECIMAL_PLACES, DECIMAL_PLACES_PERCENTAGE } from 'constants/calcConstants'
import { differenceInCalendarDays, differenceInWeeks, differenceInYears, format } from 'date-fns'

export const zeroInsteadNull = (value: any) => {
  if (value === null) return 0
  return value
}

export const formatterToUsPrice = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: DECIMAL_PLACES,
  maximumFractionDigits: DECIMAL_PLACES,
})

export const formatterToPercentage = new Intl.NumberFormat(undefined, {
  style: 'percent',
  minimumFractionDigits: 0,
  maximumFractionDigits: DECIMAL_PLACES_PERCENTAGE,
})

export const convertToPercentageFormat = (value: any): string => {
  if (value === null || value === undefined || value === '') return 'n/a'

  const num = Number(value)
  if (Number.isNaN(num)) return 'n/a'

  return formatterToPercentage.format(num / 100)
}

export const convertToPriceFormat = (value: any) => {
  if (value || value === 0) return formatterToUsPrice.format(value)
  return '$0.00'
}

export const convertToPriceFormatWithSign = (value: number) => {
  const isNegative = value < 0 || (value === 0 && 1 / value === -Infinity)
  if (value || value === 0) return `${isNegative ? '' : '+'}${formatterToUsPrice.format(value)}`
  return '$0.00'
}

export const checkForNaN = (value: any) => {
  if (Number.isNaN(value)) return 'N/A'
  if (value || value === 0) return formatterToUsPrice.format(value)
  return '$0.00'
}

export const convertEmptyField = (value: any) => {
  if (!value && value !== 0) return 'n/a'
  return value
}

export const convertDate = (date?: any) => {
  if (date && date !== '0001-01-01T00:00:00+00:00') return format(new Date(date), 'MM.dd.yyyy')
  return 'n/a'
}

export const convertDateWithTime = (date?: any) => {
  if (date) return format(new Date(date), 'MM.dd.yyyy hh:mm aaa')
  return 'n/a'
}

export function formatCurrency(value: number | string): string {
  if (value === null || value === undefined || value === '') return ''
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: DECIMAL_PLACES,
    maximumFractionDigits: DECIMAL_PLACES,
  })
}

export function formatPercentage(value: number | string): string {
  if (value === null || value === undefined || value === '') return ''
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: DECIMAL_PLACES_PERCENTAGE,
  })
}

export const convertNotifDate = (date?: any): string => {
  if (date) {
    if (differenceInCalendarDays(new Date(), new Date(date)) < 1) {
      return format(new Date(date), 'HH:mm')
    }

    if (differenceInCalendarDays(new Date(), new Date(date)) === 1) {
      return `Yesterday ${format(new Date(date), 'HH:mm')}`
    }

    if (differenceInWeeks(new Date(), new Date(date)) < 1) {
      return format(new Date(date), 'EEE HH:mm')
    }

    if (differenceInYears(new Date(), new Date(date)) < 1) {
      return format(new Date(date), 'MMM d HH:mm')
    }

    return format(new Date(date), 'dd.MM.yyyy HH:mm')
  }

  return 'n/a'
}

export const convertNotifBodyDate = (date?: any): string => {
  if (date) {
    if (differenceInYears(new Date(), new Date(date)) < 1) {
      return `${format(new Date(date), 'MMMM d')}      ${format(new Date(date), 'HH:mm')}`
    }

    return `${format(new Date(date), 'dd.MM.yyyy')}      ${format(new Date(date), 'HH:mm')}`
  }

  return 'n/a'
}

export const convertNotifSentBodyDate = (date?: any): string => {
  if (date) {
    return `${format(new Date(date), 'dd.MM.yyyy')} ${format(new Date(date), 'HH:mm')}`
  }

  return 'n/a '
}

export const formatNegativePrice = (value: number, formatter: (val: any) => any | undefined = convertToPriceFormat): string => {
  return value >= 0 ? formatter(value) : `(${formatter(Math.abs(value))})`
}

export const roundNumber = (value?: number) => {
  if (value) return Math.round((value + Number.EPSILON) * 100) / 100
  return 0
}

export const camelCaseWrap = (text: string) => {
  return text.replace(/([a-z])([A-Z])/g, '$1\u200B$2')
}

export const mask = (str: string, sign: string = '*', showDigits: number = 4): string => {
  return str.length > 4 ? '*'.repeat(str.length - 4) + str.slice(-4) : str
}

export const formatTemplate = (template: string, ...values: any[]): string => {
  return template.replace(/{(\d+)}/g, (_, index) => values[index] ?? '')
}

// #region: This formatter is commented out because it is not used in the current codebase.

// export const formatterToUsPriceWithoutCents = new Intl.NumberFormat('en-US', {
//   style: 'currency',
//   currency: 'USD',
//   minimumFractionDigits: 0,
//   maximumFractionDigits: 0,
// })

// export const convertToPriceFormat = (value: any) => {
//   if (value || value === 0) return formatterToUsPriceWithoutCents.format(value)
//   return '$0'
// }

// #endregion
